import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, ArrowUpRight, ArrowDownLeft, Wallet as WalletIcon, Copy, PaperPlaneTilt } from "@phosphor-icons/react";
import { RecommendedForYou } from "@/components/RecommendedForYou";

const PRESETS = [500, 1000, 2500, 5000, 10000];

export default function Wallet() {
  const qc = useQueryClient();
  const { data: wallet } = useQuery({ queryKey: ["wallet"], queryFn: async () => (await api.get("/wallet")).data });
  const { data: ledger } = useQuery({ queryKey: ["ledger"], queryFn: async () => (await api.get("/wallet/ledger")).data });
  const [amount, setAmount] = useState(500);
  const [pending, setPending] = useState(null); // "card" | "paypal" | null
  const [notifying, setNotifying] = useState(false);
  const [flwCurrency, setFlwCurrency] = useState("NGN");
  const [flwBusy, setFlwBusy] = useState(false);
  const { data: flwInfo } = useQuery({ queryKey: ["flw-currencies"], queryFn: async () => (await api.get("/flutterwave/currencies")).data });
  const entries = ledger?.entries || [];

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const txRef = p.get("flw");
    const txId = p.get("transaction_id");
    if (txRef && txId) {
      api.get(`/flutterwave/confirm?tx_ref=${encodeURIComponent(txRef)}&transaction_id=${encodeURIComponent(txId)}`)
        .then(() => {
          toast.success("Wallet funded via Mobile Money / Card");
          qc.invalidateQueries({ queryKey: ["wallet"] });
          qc.invalidateQueries({ queryKey: ["ledger"] });
        })
        .catch((e) => toast.error(apiError(e)))
        .finally(() => window.history.replaceState({}, "", "/app/wallet"));
    }
  }, [qc]);

  const addFunds = async () => {
    if (amount <= 0) return;
    setPending("card");
    try {
      const { data } = await api.post("/payments/checkout", { amount_cents: amount, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setPending(null); }
  };

  const addFundsPaypal = async () => {
    if (amount <= 0) return;
    setPending("paypal");
    try {
      const { data } = await api.post("/payments/paypal/create-order", { amount_cents: amount, origin_url: window.location.origin });
      window.location.href = data.approve_url;
    } catch (e) { toast.error(apiError(e)); setPending(null); }
  };

  const addFundsFlutterwave = async () => {
    if (amount <= 0) return;
    setFlwBusy(true);
    try {
      const { data } = await api.post("/flutterwave/fund", { amount_cents: amount, currency: flwCurrency });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setFlwBusy(false); }
  };

  const notifyPaid = async (method) => {    setNotifying(true);
    try {
      await api.post("/wallet/manual-payment-notify", { method, amount_cents: amount });
      toast.success("Thanks! We've been notified and will credit your wallet shortly.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setNotifying(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Universal Wallet</h1>
        <p className="mt-2 text-muted-foreground">One balance. Immutable ledger. Every transaction recorded.</p>
      </div>

      <RecommendedForYou />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* balance + add funds */}
        <div className="space-y-6">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#08111F] p-6 text-white">
            <div className="grain absolute inset-0" />
            <div className="relative">
              <p className="text-xs uppercase tracking-[0.2em] text-white/50">Available balance</p>
              <p data-testid="wallet-balance" className="mt-2 font-mono-metric text-4xl font-semibold">
                {wallet ? money(wallet.balance_cents, wallet.currency) : "—"}
              </p>
            </div>
          </div>

          <Link to="/app/send" data-testid="wallet-send-money"
            className="flex items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 transition-colors hover:border-primary">
            <span className="flex items-center gap-2 font-semibold"><PaperPlaneTilt size={18} weight="fill" className="text-primary" /> Send money to a member</span>
            <span className="text-primary">→</span>
          </Link>

          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-semibold">Add Funds</h2>
            <p className="mt-1 text-xs text-muted-foreground">Secured card checkout. Add funds to your wallet.</p>
            <div className="mt-4 grid grid-cols-5 gap-2">
              {PRESETS.map((p) => (
                <button key={p} data-testid={`preset-${p}`} onClick={() => setAmount(p)}
                  className={`rounded-xl border py-2 text-sm font-semibold transition-colors ${amount === p ? "border-primary bg-accent text-accent-foreground" : "border-border"}`}>
                  ${p / 100}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-input px-3">
              <span className="text-muted-foreground">$</span>
              <input data-testid="custom-amount" type="number" min="1" value={(amount / 100).toString()}
                onChange={(e) => setAmount(Math.round(Number(e.target.value) * 100))}
                className="w-full bg-transparent py-3 outline-none" />
            </div>
            <button data-testid="confirm-add-funds" onClick={addFunds} disabled={pending !== null}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
              <Plus size={18} weight="bold" /> {pending === "card" ? "Redirecting…" : `Pay by card · ${money(amount)}`}
            </button>
            <button data-testid="add-funds-paypal" onClick={addFundsPaypal} disabled={pending !== null}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#ffc439] py-3.5 font-semibold text-[#003087] transition-transform hover:scale-[1.02] disabled:opacity-60">
              {pending === "paypal" ? "Redirecting…" : `Pay with PayPal · ${money(amount)}`}
            </button>

            <div className="mt-3 border-t border-border pt-3" data-testid="flutterwave-fund">
              <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Mobile Money / Card</h3>
              <p className="mt-1 text-xs text-muted-foreground">Pay in your local currency — we convert and credit your wallet in USD.</p>
              <div className="mt-2 flex gap-2">
                <select data-testid="flw-currency" value={flwCurrency} onChange={(e) => setFlwCurrency(e.target.value)}
                  className="rounded-xl border border-input bg-background px-3 py-2.5 text-sm">
                  {(flwInfo?.currencies || ["NGN"]).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button data-testid="add-funds-flutterwave" onClick={addFundsFlutterwave} disabled={flwBusy || !flwInfo?.configured}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#f5a623] py-2.5 text-sm font-semibold text-[#2b1a00] transition-transform hover:scale-[1.01] disabled:opacity-60">
                  {flwBusy ? "Redirecting…" : `Pay with Mobile Money / Card · ${money(amount)}`}
                </button>
              </div>
              {flwInfo && !flwInfo.configured && <p className="mt-1 text-xs text-amber-600" data-testid="flw-not-configured">Mobile Money is being set up.</p>}
            </div>

            <div className="mt-5 border-t border-border pt-4" data-testid="manual-payment">
              <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Manual Payment Process</h3>
              <p className="mt-1 text-xs text-muted-foreground">Prefer to pay manually? Send to one of the below, then message us on WhatsApp with your receipt and account email.</p>
              <div className="mt-3 space-y-2">
                {[
                  { label: "MTN MoMo", value: "237653244130", note: "iYeneh" },
                  { label: "WhatsApp", value: "+1 202 940 3399", copy: "+12029403399", href: "https://wa.me/12029403399" },
                ].map((m) => (
                  <div key={m.label} data-testid={`manual-${m.label.split(" ")[0].toLowerCase()}`}
                    className="flex items-center justify-between gap-2 rounded-xl border border-border bg-background px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold">{m.label}</p>
                      <p className="truncate font-mono-metric text-sm">
                        {m.href
                          ? <a href={m.href} target="_blank" rel="noreferrer" className="text-primary hover:underline">{m.value}</a>
                          : m.value}
                        {m.note && <span className="ml-2 text-xs text-muted-foreground">{m.note}</span>}
                      </p>
                    </div>
                    <button data-testid={`copy-${m.label.split(" ")[0].toLowerCase()}`}
                      onClick={() => { navigator.clipboard.writeText(m.copy || m.value); toast.success(`${m.label} copied`); }}
                      className="shrink-0 rounded-lg border border-border p-2 text-muted-foreground hover:text-foreground">
                      <Copy size={15} />
                    </button>
                  </div>
                ))}
              </div>
              <button data-testid="manual-paid-notify" onClick={() => notifyPaid("manual")} disabled={notifying}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-primary bg-accent py-2.5 text-sm font-semibold text-primary transition-transform hover:scale-[1.01] disabled:opacity-60">
                {notifying ? "Notifying…" : "I've paid manually — notify us"}
              </button>
            </div>
          </div>
        </div>

        {/* ledger */}
        <div className="lg:col-span-2">
          <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <WalletIcon size={14} /> Immutable Ledger
          </h2>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            {entries.length === 0 && <p className="px-4 py-10 text-center text-sm text-muted-foreground">No ledger entries yet</p>}
            {entries.map((e, i) => (
              <div key={i} data-testid={`ledger-row-${i}`} className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-0">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${e.direction === "credit" ? "bg-emerald-100 text-emerald-600" : "bg-accent text-primary"}`}>
                  {e.direction === "credit" ? <ArrowDownLeft size={18} weight="bold" /> : <ArrowUpRight size={18} weight="bold" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium capitalize">{e.product?.replace(/_/g, " ")}</p>
                  <p className="truncate text-xs text-muted-foreground">{e.provider} · {shortDate(e.created_at)}{e.reference ? ` · ${e.reference}` : ""}</p>
                </div>
                <div className="text-right">
                  <p className={`font-mono-metric text-sm font-semibold ${e.direction === "credit" ? "text-emerald-600" : ""}`}>
                    {e.direction === "credit" ? "+" : "−"}{money(e.amount_cents, e.currency)}
                  </p>
                  <p className="font-mono-metric text-xs text-muted-foreground">bal {money(e.balance_after_cents, e.currency)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
